import { Trophy, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { StandingEntry } from "@/hooks/useStandingsCache";
import { WORLD_CUP_API_LEAGUE_ID, WORLD_CUP_OFFICIAL_LOGO } from "@/lib/worldCup2026";
import { LEAGUE_QUALIFICATION_ZONES } from "@/lib/constants";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface StandingsTableProps {
  standings: StandingEntry[];
  leagueName?: string;
  leagueApiId?: number;
  season?: number;
  className?: string;
  leagueLogo?: string;
  highlightTeamIds?: number[];
}

export function StandingsTable({
  standings,
  leagueName,
  leagueApiId,
  season = 2025,
  className,
  leagueLogo,
  highlightTeamIds
}: StandingsTableProps) {

  const getPositionIcon = (rank: number, previousRank?: number) => {
    if (!previousRank) return null;
    
    if (rank < previousRank) {
      return <TrendingUp size={12} className="text-green-600" />;
    } else if (rank > previousRank) {
      return <TrendingDown size={12} className="text-red-600" />;
    } else {
      return <Minus size={12} className="text-gray-medium" />;
    }
  };

  const getPositionColor = (rank: number, totalTeams: number) => {
    if (!leagueApiId) return "text-foreground";
    
    const config = LEAGUE_QUALIFICATION_ZONES[leagueApiId];
    if (!config) return "text-foreground";
    
    for (const zone of config.zones) {
      // Converter posições negativas (ex: -1 = última posição)
      const absolutePositions = zone.positions.map(pos => 
        pos < 0 ? totalTeams + pos + 1 : pos
      );
      
      if (absolutePositions.includes(rank)) {
        return getPositionColorClass(zone.color);
      }
    }
    
    return "text-foreground";
  };

  const getPositionColorClass = (color: string): string => {
    const colorMap: Record<string, string> = {
      'green': 'text-green-600',
      'emerald': 'text-emerald-600',
      'blue': 'text-blue-600',
      'cyan': 'text-cyan-600',
      'orange': 'text-orange-600',
      'red': 'text-red-600'
    };
    return colorMap[color] || 'text-foreground';
  };

  const getZoneColorClass = (color: string): string => {
    const colorMap: Record<string, string> = {
      'green': 'bg-green-600',
      'emerald': 'bg-emerald-600',
      'blue': 'bg-blue-600',
      'cyan': 'bg-cyan-600',
      'orange': 'bg-orange-600',
      'red': 'bg-red-600'
    };
    return colorMap[color] || 'bg-gray-600';
  };

  return (
    <div className={cn("sketch-border bg-card overflow-hidden", className)}>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-light hover:bg-paper-sketch">
              <TableHead className="w-12 text-center">
                {(() => {
                  const effectiveLogo = leagueApiId === WORLD_CUP_API_LEAGUE_ID ? WORLD_CUP_OFFICIAL_LOGO : leagueLogo;
                  return effectiveLogo ? (
                    <img
                      src={effectiveLogo}
                      alt="Liga"
                      className="w-6 h-6 object-contain mx-auto"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="font-bold">#</span>
                  );
                })()}
              </TableHead>
              <TableHead className="font-bold">Team</TableHead>
              <TableHead className="w-16 text-center font-bold">P</TableHead>
              <TableHead className="w-16 text-center font-bold">W</TableHead>
              <TableHead className="w-16 text-center font-bold">D</TableHead>
              <TableHead className="w-16 text-center font-bold">L</TableHead>
              <TableHead className="w-20 text-center font-bold">GF</TableHead>
              <TableHead className="w-20 text-center font-bold">GA</TableHead>
              <TableHead className="w-20 text-center font-bold">GD</TableHead>
              <TableHead className="w-16 text-center font-bold">Pts</TableHead>
              <TableHead className="w-20 text-center font-bold">Form</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {standings.map((team, index) => {
              const isHighlighted = highlightTeamIds?.includes(team.team.id);
              return (
              <TableRow
                key={team.team.id}
                className={cn(
                  "border-gray-light hover:bg-paper-sketch transition-colors",
                  isHighlighted
                    ? "bg-primary/10 font-semibold"
                    : index % 2 === 0 ? "bg-background" : "bg-paper-aged"
                )}
              >
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <span className={cn("font-bold", getPositionColor(team.rank, standings.length))}>
                      {team.rank}
                    </span>
                    {getPositionIcon(team.rank)}
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <img
                      src={team.team.logo}
                      alt={`${team.team.name} logo`}
                      className="w-5 h-5 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <span className="font-medium text-sm truncate">
                      {team.team.name}
                    </span>
                  </div>
                </TableCell>
                
                <TableCell className="text-center font-medium">
                  {team.all.played}
                </TableCell>
                
                <TableCell className="text-center font-medium text-green-600">
                  {team.all.win}
                </TableCell>
                
                <TableCell className="text-center font-medium text-gray-medium">
                  {team.all.draw}
                </TableCell>
                
                <TableCell className="text-center font-medium text-red-600">
                  {team.all.lose}
                </TableCell>
                
                <TableCell className="text-center font-medium">
                  {team.all.goals.for}
                </TableCell>
                
                <TableCell className="text-center font-medium">
                  {team.all.goals.against}
                </TableCell>
                
                <TableCell className={cn(
                  "text-center font-bold",
                  team.goalsDiff > 0 ? "text-green-600" : 
                  team.goalsDiff < 0 ? "text-red-600" : "text-gray-medium"
                )}>
                  {team.goalsDiff > 0 ? `+${team.goalsDiff}` : team.goalsDiff}
                </TableCell>
                
                <TableCell className="text-center font-bold text-primary">
                  {team.points}
                </TableCell>
                
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-0.5">
                    {(team.form || '').split('').slice(-5).map((result, i) => (
                      <div
                        key={i}
                        className={cn(
                          "w-4 h-4 rounded-sm flex items-center justify-center text-xs font-bold",
                          result === 'W' && "bg-green-600 text-white",
                          result === 'D' && "bg-gray-medium text-white",
                          result === 'L' && "bg-red-600 text-white"
                        )}
                      >
                        {result}
                      </div>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Legend - Dinâmica por Liga */}
      {leagueApiId && LEAGUE_QUALIFICATION_ZONES[leagueApiId] && (
        <div className="p-3 border-t border-gray-light bg-paper-sketch">
          <div className="flex flex-wrap gap-4 text-xs">
            {LEAGUE_QUALIFICATION_ZONES[leagueApiId]!.zones.map((zone, index) => (
              <div key={index} className="flex items-center gap-1">
                <div className={cn("w-3 h-3 rounded", getZoneColorClass(zone.color))}></div>
                <span className="font-medium">{zone.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}